import type { AccessCatalogItemPublic, AccessLevelPublic, AccessNodePublic } from '@accounts/shared';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { STR } from '../../../../i18n';
import {
  createAccessLevel,
  createAccessNode,
  renameAccessLevel,
  retireAccessNode,
  updateAccessNode,
} from '../../../../lib/api';
import { formatDate } from '../../../../lib/format';
import { showToast } from '../../../../lib/toast';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../../../ui/Modal';
import { Callout, Dot, EmptyRow } from '../../../../ui/Primitives';
import {
  eyebrowStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  rowActionStyle,
  textFieldStyle,
} from '../../../../ui/styles';

import { childrenOf, parentChoices, pathText, rowShort } from './hierarchy';
import { errorText, useInvalidateAccess } from './queries';

/*
 * The hierarchy's admin settings, as the FINAL demo lays them out on the catalog page: the
 * Hierarchy section names the levels (top first) and shows how the catalog is filed now;
 * then one section per level lists that level's entries — rename in place, move, retire,
 * add. Levels are only added and renamed (the server has no level retire); entries are
 * retired, never deleted, and a retired entry stays listed, faded, with its date.
 */

const smallField = { padding: '6px 9px', borderRadius: 7, fontSize: 12.5 } as const;

/** Live catalog rows filed on `nodeId` itself or anywhere below it. */
function rowsUnder(items: readonly AccessCatalogItemPublic[], nodeId: string): number {
  return items.filter(
    (item) => item.retiredAt === null && item.path.some((step) => step.id === nodeId),
  ).length;
}

export function LevelsEditor({
  levels,
  nodes,
  items,
}: {
  levels: readonly AccessLevelPublic[];
  nodes: readonly AccessNodePublic[];
  items: readonly AccessCatalogItemPublic[];
}) {
  const invalidate = useInvalidateAccess();
  const [names, setNames] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameAccessLevel(id, name),
    onSuccess: async (response, { id }) => {
      setNames((state) => {
        const next = { ...state };
        delete next[id];
        return next;
      });
      setError(null);
      await invalidate();
      showToast(STR.access.catalog.levelRenamed(response.level.name));
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const add = useMutation({
    mutationFn: (name: string) => createAccessLevel(name),
    onSuccess: async (response) => {
      setNewName('');
      setError(null);
      await invalidate();
      showToast(STR.access.catalog.levelAdded(response.level.name));
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const rowsStopping = (levelId: string): number =>
    items.filter((item) => {
      const last = item.path[item.path.length - 1];
      return item.retiredAt === null && last !== undefined && last.level.id === levelId;
    }).length;

  const live = nodes.filter((node) => node.retiredAt === null);
  const roots = childrenOf(nodes, null);
  const notFiled = items.filter(
    (item) => item.retiredAt === null && item.path.length === 0 && item.application === null,
  );

  return (
    <div>
      <p
        className="tx-muted"
        style={{ margin: 0, padding: '12px 18px 8px', fontSize: 12.5, lineHeight: 1.55 }}
      >
        {STR.access.catalog.hierarchyLead}
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, borderTop: '1px solid var(--border)' }}>
        {levels.length === 0 ? (
          <li>
            <EmptyRow>{STR.access.catalog.noLevels}</EmptyRow>
          </li>
        ) : null}
        {levels.map((level) => {
          const value = names[level.id] ?? level.name;
          const submit = () => {
            if (value.trim() === '' || value.trim() === level.name) return;
            rename.mutate({ id: level.id, name: value.trim() });
          };
          return (
            <li
              key={level.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '70px minmax(160px,260px) auto minmax(0,1fr)',
                gap: 10,
                alignItems: 'center',
                padding: '9px 18px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 650 }}>
                {STR.access.catalog.levelNumber(level.depth)}
              </span>
              <input
                className="field"
                type="text"
                value={value}
                maxLength={200}
                aria-label={STR.access.catalog.levelNameLabel(level.depth)}
                onChange={(event) => {
                  setNames((state) => ({ ...state, [level.id]: event.target.value }));
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submit();
                }}
                style={smallField}
              />
              <button
                type="button"
                className="tbtn"
                style={rowActionStyle}
                disabled={rename.isPending || value.trim() === '' || value.trim() === level.name}
                onClick={submit}
              >
                {STR.access.catalog.save}
              </button>
              <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                {STR.access.catalog.levelUsage(
                  live.filter((node) => node.level.id === level.id).length,
                  rowsStopping(level.id),
                )}
              </span>
            </li>
          );
        })}
        <li
          style={{
            display: 'grid',
            gridTemplateColumns: '70px minmax(160px,260px) auto minmax(0,1fr)',
            gap: 10,
            alignItems: 'center',
            padding: '9px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 650 }}>
            {STR.access.catalog.levelNumber(levels.length + 1)}
          </span>
          <input
            className="field"
            type="text"
            value={newName}
            maxLength={200}
            placeholder={STR.access.catalog.addLevelPlaceholder}
            aria-label={STR.access.catalog.addLevelLabel}
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim() !== '') add.mutate(newName.trim());
            }}
            style={smallField}
          />
          <button
            type="button"
            className="ghost"
            style={rowActionStyle}
            disabled={add.isPending || newName.trim() === ''}
            onClick={() => {
              add.mutate(newName.trim());
            }}
          >
            {STR.access.catalog.addLevel}
          </button>
          <span className="tx-subtle" style={{ fontSize: 11.5 }}>
            {STR.access.catalog.addLevelHint}
          </span>
        </li>
      </ul>
      {error === null ? null : (
        <div style={{ padding: '10px 18px 0' }}>
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        </div>
      )}
      <div style={{ padding: '12px 18px 14px' }}>
        <div className="tx-subtle" style={eyebrowStyle}>
          {STR.access.catalog.filedNowTitle}
        </div>
        <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
          {roots.map((root) => {
            const rows = items.filter(
              (item) => item.retiredAt === null && item.path[0]?.id === root.id,
            );
            if (rows.length === 0) return null;
            return (
              <li
                key={root.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px minmax(0,1fr)',
                  gap: 12,
                  padding: '7px 0',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12.5,
                }}
              >
                <b style={{ fontWeight: 650 }}>{root.name}</b>
                <span style={{ lineHeight: 1.55 }}>
                  {rows
                    .map(rowShort)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ')}
                </span>
              </li>
            );
          })}
          {notFiled.length === 0 ? null : (
            <li
              style={{
                display: 'grid',
                gridTemplateColumns: '150px minmax(0,1fr)',
                gap: 12,
                padding: '7px 0',
                fontSize: 12.5,
              }}
            >
              <b className="tx-subtle" style={{ fontWeight: 650 }}>
                {STR.access.catalog.notFiledGroup}
              </b>
              <span>{notFiled.map((item) => item.name).join(', ')}</span>
            </li>
          )}
        </ul>
        <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 8 }}>
          {STR.access.catalog.suiteAppsNotFiled}
        </div>
      </div>
    </div>
  );
}

type NodeDialog = { kind: 'move'; node: AccessNodePublic } | { kind: 'retire'; node: AccessNodePublic };

/** One level's entries: rename in place, move, retire, and add a new one. */
export function LevelNodes({
  level,
  nodes,
  items,
}: {
  level: AccessLevelPublic;
  nodes: readonly AccessNodePublic[];
  items: readonly AccessCatalogItemPublic[];
}) {
  const invalidate = useInvalidateAccess();
  const [names, setNames] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [newParent, setNewParent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<NodeDialog | null>(null);
  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);

  const atLevel = nodes
    .filter((node) => node.level.id === level.id)
    .sort(
      (a, b) =>
        (a.retiredAt === null ? 0 : 1) - (b.retiredAt === null ? 0 : 1) ||
        pathText(a).localeCompare(pathText(b), undefined, { sensitivity: 'base' }),
    );
  const parents = parentChoices(nodes, level.depth);

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateAccessNode(id, { name }),
    onSuccess: async (response, { id }) => {
      setNames((state) => {
        const next = { ...state };
        delete next[id];
        return next;
      });
      setError(null);
      await invalidate();
      showToast(STR.access.catalog.nodeRenamed(response.node.name));
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const add = useMutation({
    mutationFn: () =>
      createAccessNode({
        name: newName.trim(),
        levelId: level.id,
        parentId: newParent === '' ? null : newParent,
      }),
    onSuccess: async (response) => {
      setNewName('');
      setError(null);
      await invalidate();
      showToast(STR.access.catalog.nodeAdded(response.node.name));
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const retire = useMutation({
    mutationFn: (node: AccessNodePublic) => retireAccessNode(node.id),
    onSuccess: async (response) => {
      setDialog(null);
      await invalidate();
      showToast(STR.access.catalog.nodeRetired(response.node.name));
    },
    onError: (failure) => {
      setDialog(null);
      showToast(errorText(failure), 'warn');
    },
  });

  return (
    <div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {atLevel.length === 0 ? (
          <li>
            <EmptyRow>{STR.access.catalog.noNodes(level.name)}</EmptyRow>
          </li>
        ) : null}
        {atLevel.map((node) => {
          const parent = node.path[node.path.length - 2];
          const where = parent === undefined ? STR.access.catalog.nodeTop : STR.access.catalog.nodeUnder(parent.name);
          if (node.retiredAt !== null) {
            return (
              <li
                key={node.id}
                data-off=""
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 18px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12.5,
                }}
              >
                <Dot tone="neutral" />
                <span className="tx-subtle" style={{ flex: 1 }}>
                  {node.name}{' '}
                  <span style={{ fontSize: 11.5 }}>{where}</span>
                </span>
                <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                  {STR.access.catalog.retiredNode(formatDate(node.retiredAt))}
                </span>
              </li>
            );
          }
          const value = names[node.id] ?? node.name;
          const submit = () => {
            if (value.trim() === '' || value.trim() === node.name) return;
            rename.mutate({ id: node.id, name: value.trim() });
          };
          return (
            <li
              key={node.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '6px 18px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <Dot tone="ok" />
              <input
                className="field"
                type="text"
                value={value}
                maxLength={200}
                aria-label={STR.access.catalog.nodeNameLabel(node.name)}
                onChange={(event) => {
                  setNames((state) => ({ ...state, [node.id]: event.target.value }));
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submit();
                }}
                style={{ ...smallField, flex: 1, minWidth: 0 }}
              />
              <span className="tx-subtle" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                {where}
              </span>
              <span
                className="tx-subtle"
                style={{
                  fontSize: 11.5,
                  width: 64,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {STR.access.catalog.nodeRows(rowsUnder(items, node.id))}
              </span>
              <button
                type="button"
                className="tbtn"
                style={rowActionStyle}
                disabled={rename.isPending || value.trim() === '' || value.trim() === node.name}
                onClick={submit}
              >
                {STR.access.catalog.save}
              </button>
              <button
                type="button"
                className="tbtn"
                style={rowActionStyle}
                onClick={() => {
                  setDialog({ kind: 'move', node });
                }}
              >
                {STR.access.catalog.move}
              </button>
              <button
                type="button"
                className="tbtn tbtn-danger"
                style={rowActionStyle}
                onClick={() => {
                  setDialog({ kind: 'retire', node });
                }}
              >
                {STR.access.catalog.retire}
              </button>
            </li>
          );
        })}
      </ul>
      {error === null ? null : (
        <div style={{ padding: '10px 18px 0' }}>
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 18px', flexWrap: 'wrap' }}>
        <input
          className="field"
          type="text"
          value={newName}
          maxLength={200}
          placeholder={STR.access.catalog.newNodePlaceholder}
          aria-label={STR.access.catalog.newNodeLabel(level.name)}
          onChange={(event) => {
            setNewName(event.target.value);
          }}
          style={{ ...smallField, flex: 1, minWidth: 160 }}
        />
        {parents.length === 0 ? null : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span className="tx-subtle">{STR.access.catalog.parentLabel}</span>
            <select
              className="field"
              value={newParent}
              onChange={(event) => {
                setNewParent(event.target.value);
              }}
              style={{ ...smallField, paddingRight: 26 }}
            >
              <option value="">{STR.access.catalog.parentTop}</option>
              {parents.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {pathText(parent)}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className="ghost"
          style={rowActionStyle}
          disabled={add.isPending || newName.trim() === ''}
          onClick={() => {
            add.mutate();
          }}
        >
          {STR.access.catalog.addNode(level.name)}
        </button>
      </div>
      <div className="tx-subtle" style={{ ...hintStyle, padding: '0 18px 12px', marginTop: 0 }}>
        {STR.access.catalog.nodeHint}
      </div>

      {dialog?.kind === 'move' ? (
        <MoveNodeDialog node={dialog.node} parents={parents} onClose={closeDialog} />
      ) : null}
      {dialog?.kind === 'retire' ? (
        <Modal
          title={STR.access.catalog.retireNodeTitle(dialog.node.name)}
          onClose={closeDialog}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={closeDialog}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={retire.isPending}
                onClick={() => {
                  retire.mutate(dialog.node);
                }}
              >
                {STR.access.catalog.retireConfirm}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.access.catalog.retireNodeBody}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}
    </div>
  );
}

function MoveNodeDialog({
  node,
  parents,
  onClose,
}: {
  node: AccessNodePublic;
  parents: readonly AccessNodePublic[];
  onClose: () => void;
}) {
  const invalidate = useInvalidateAccess();
  const [parentId, setParentId] = useState(node.parentId ?? '');
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: () => updateAccessNode(node.id, { parentId: parentId === '' ? null : parentId }),
    onSuccess: async (response) => {
      await invalidate();
      showToast(STR.access.catalog.nodeMoved(response.node.name));
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const unchanged = parentId === (node.parentId ?? '');

  return (
    <Modal
      title={STR.access.catalog.moveTitle(node.name)}
      lead={STR.access.catalog.moveLead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={move.isPending || unchanged}
            onClick={() => {
              move.mutate();
            }}
          >
            {STR.access.catalog.moveConfirm}
          </button>
        </>
      }
    >
      <ModalForm>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.access.catalog.parentLabel}
          </span>
          <select
            className="field"
            value={parentId}
            onChange={(event) => {
              setParentId(event.target.value);
              setError(null);
            }}
            style={textFieldStyle}
          >
            <option value="">{STR.access.catalog.parentTop}</option>
            {parents.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {pathText(parent)}
              </option>
            ))}
          </select>
        </label>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}
